import { Deferred, Head } from "@inertiajs/react";
import {
	AlertTriangle,
	Banknote,
	CalendarCheck,
	HandPlatter,
	MapPinned,
	ShieldCheck,
	Stethoscope,
	Timer,
} from "lucide-react";
import { useMemo } from "react";
import {
	AdminByRoleChart,
	TherapistByEmploymentTypeChart,
	TherapistByGenderChart,
} from "@/components/admin-portal/dashboard/charts";
import StatCard from "@/components/admin-portal/dashboard/stat-card";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { Skeleton } from "@/components/ui/skeleton";

export interface DashboardAdmins {
	total: number;
	active: number;
	by_type: Record<string, number>;
}
export interface DashboardTherapists {
	total: number;
	active: number;
	by_gender: Record<string, number>;
	by_employment_type: Record<string, number>;
}
export interface DashboardServices {
	active: number;
	location_registered: {
		active: number;
	};
}
export interface DashboardLocations {
	total: number;
	active: number;
	top_5_cities_with_therapists: Record<string, number>;
}
export interface DashboardDaily {
	scheduled_today: number;
	pending_assignment_stuck: number;
	attention_needed_today: number;
	revenue_today: number;
}
export interface DashboardWeekly {
	total_appointments_this_week: number;
	total_appointments_last_week: number;
	total_appointments_change_pct: number;
	completed_this_week: number;
	completed_last_week: number;
	completion_rate: number;
	completion_rate_last_week: number;
	completion_rate_change_pct: number;
	cancelled_this_week: number;
	cancelled_last_week: number;
	cancellation_rate: number;
	cancellation_rate_last_week: number;
	cancellation_rate_change_pct: number;
	revenue_this_week: number;
	revenue_last_week: number;
	revenue_change_pct: number;
}
export interface PageProps {
	admins: DashboardAdmins;
	therapists: DashboardTherapists;
	services: DashboardServices;
	locations: DashboardLocations;
	daily: DashboardDaily;
	weekly: DashboardWeekly;
}

export default function Index({
	admins,
	locations,
	services,
	therapists,
	daily,
	weekly,
}: PageProps) {
	const dailyStats = useMemo(() => {
		const scheduledToday = {
			title: "Scheduled Today",
			data: daily?.scheduled_today || 0,
			icon: CalendarCheck,
			additional: "Appointments for today",
			description:
				"Count of appointments scheduled for today with PAID or SCHEDULED status. Includes all visits booked for the current date.",
		};
		const attentionNeeded = {
			title: "Appointments Needing Attention",
			data: daily?.attention_needed_today || 0,
			icon: AlertTriangle,
			additional: "Today, not yet ready",
			description:
				"Today's appointments with status not yet PAID, SCHEDULED, or COMPLETED. These require admin action (e.g. assign therapist, confirm payment, resolve issues).",
		};
		const pendingAssignment = {
			title: "Pending Assignment > 24h",
			data: daily?.pending_assignment_stuck || 0,
			icon: Timer,
			additional: "Stuck awaiting therapist",
			description:
				"Appointments stuck in PENDING THERAPIST ASSIGNMENT status for more than 24 hours. Requires admin intervention to assign a therapist.",
		};
		const revenueToday = {
			title: "Revenue Today",
			data: daily?.revenue_today || 0,
			icon: Banknote,
			additional: "Booked for today",
			description:
				"Total expected revenue from all appointments scheduled for today (excluding CANCELLED). Uses order total_amount when available; falls back to package price_per_visit.",
			formatter: (value: number) =>
				new Intl.NumberFormat("id-ID", {
					style: "currency",
					currency: "IDR",
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				}).format(value),
		};
		return [scheduledToday, attentionNeeded, pendingAssignment, revenueToday];
	}, [daily]);

	const weeklyStats = useMemo(() => {
		const totalAppointments = {
			title: "Total Appointments",
			data: weekly?.total_appointments_this_week || 0,
			icon: CalendarCheck,
			additional: weekly?.total_appointments_change_pct
				? `${weekly.total_appointments_change_pct >= 0 ? "+" : ""}${weekly.total_appointments_change_pct}% vs last week`
				: `${weekly?.total_appointments_last_week || 0} last week`,
			description:
				"Total appointments scheduled during this week (Mon–Sun) with any status. Compares against the previous week.",
		};
		const completionRate = {
			title: "Completion Rate",
			data: weekly?.completion_rate || 0,
			icon: ShieldCheck,
			additional: weekly?.completion_rate_change_pct
				? `${weekly.completion_rate_change_pct >= 0 ? "+" : ""}${weekly.completion_rate_change_pct}% vs last week`
				: `${weekly?.completion_rate_last_week || 0}% last week`,
			description:
				"Percentage of appointments marked COMPLETED out of all appointments this week. Higher is better.",
			formatter: (value: number) => `${value}%`,
		};
		const weeklyRevenue = {
			title: "Weekly Revenue",
			data: weekly?.revenue_this_week || 0,
			icon: Banknote,
			additional: weekly?.revenue_change_pct
				? `${weekly.revenue_change_pct >= 0 ? "+" : ""}${weekly.revenue_change_pct}% vs last week`
				: `${new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(weekly?.revenue_last_week || 0)} last week`,
			description:
				"Total revenue from COMPLETED appointments this week. Uses order total_amount linked by registration_number.",
			formatter: (value: number) =>
				new Intl.NumberFormat("id-ID", {
					style: "currency",
					currency: "IDR",
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				}).format(value),
		};
		const cancellationRate = {
			title: "Cancellation Rate",
			data: weekly?.cancellation_rate || 0,
			icon: AlertTriangle,
			additional: weekly?.cancellation_rate_change_pct
				? `${weekly.cancellation_rate_change_pct >= 0 ? "+" : ""}${weekly.cancellation_rate_change_pct}% vs last week`
				: `${weekly?.cancellation_rate_last_week || 0}% last week`,
			description:
				"Percentage of appointments cancelled this week. Lower is better. Compare against last week to spot trends.",
			formatter: (value: number) => `${value}%`,
		};

		return [totalAppointments, completionRate, cancellationRate, weeklyRevenue];
	}, [weekly]);

	const statsDashboards = useMemo(() => {
		const adminTotal = {
			title: "Active Admins",
			data: admins?.active || 0,
			icon: ShieldCheck,
			additional: `from ${admins?.total || 0} total admins`,
		};
		const therapistTotal = {
			title: "Active Therapists",
			data: therapists?.active || 0,
			icon: Stethoscope,
			additional: `from ${therapists?.total || 0} total therapists`,
		};
		const serviceActive = {
			title: "Active Services",
			data: services?.active || 0,
			icon: HandPlatter,
			additional: `in ${services?.location_registered.active || 0} location(s)`,
		};
		const locationActive = {
			title: "Active Locations",
			data: locations?.active || 0,
			icon: MapPinned,
			additional: `from ${locations?.total || 0} total locations`,
		};

		return [serviceActive, therapistTotal, locationActive, adminTotal];
	}, [therapists, services, locations, admins]);

	return (
		<>
			<Head title="Dashboard Overview" />

			<PageContainer className="flex items-center justify-between">
				<h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
			</PageContainer>

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-8">
				{/* --- Tab 1 Section A: Daily Operations Snapshot --- */}
				<section>
					<h2 className="text-lg font-semibold tracking-tight mb-3">
						Today&apos;s Operations
					</h2>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
						{dailyStats?.length &&
							dailyStats.map((stat) => (
								<Deferred
									key={stat.title}
									data="daily"
									fallback={
										<Skeleton className="col-span-full md:col-span-1 h-[125px] rounded-md" />
									}
								>
									<StatCard
										key={stat.title}
										data={stat}
										className="col-span-1 md:col-span-1"
									/>
								</Deferred>
							))}
					</div>
				</section>

				{/* --- Tab 2 Section A: Weekly Performance --- */}
				<section>
					<h2 className="text-lg font-semibold tracking-tight mb-3">
						Weekly Performance
					</h2>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
						{weeklyStats?.length &&
							weeklyStats.map((stat) => (
								<Deferred
									key={stat.title}
									data="weekly"
									fallback={
										<Skeleton className="col-span-full md:col-span-1 h-[125px] rounded-md" />
									}
								>
									<StatCard
										key={stat.title}
										data={stat}
										className="col-span-1 md:col-span-1"
									/>
								</Deferred>
							))}
					</div>
				</section>

				{/* --- Existing Master Data Overview --- */}
				<section>
					<h2 className="text-lg font-semibold tracking-tight mb-3">
						Master Data Overview
					</h2>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
						{statsDashboards?.length &&
							statsDashboards.map((stat) => (
								<Deferred
									key={stat.title}
									data={["admins", "therapists", "locations", "services"]}
									fallback={
										<Skeleton className="col-span-full md:col-span-1 h-[125px] rounded-md" />
									}
								>
									<StatCard
										key={stat.title}
										data={stat}
										className="col-span-1 md:col-span-1"
									/>
								</Deferred>
							))}

						<Deferred
							data="admins"
							fallback={
								<Skeleton className="col-span-full xl:col-span-2 h-[360px] rounded-md" />
							}
						>
							{admins?.by_type && (
								<AdminByRoleChart
									data={admins.by_type}
									className="col-span-full xl:col-span-2"
								/>
							)}
						</Deferred>

						<Deferred
							data="therapists"
							fallback={
								<Skeleton className="col-span-full md:col-span-1 h-[360px] rounded-md" />
							}
						>
							{therapists?.by_employment_type && (
								<TherapistByEmploymentTypeChart
									data={therapists.by_employment_type}
									className="col-span-full md:col-span-1"
								/>
							)}
						</Deferred>

						<Deferred
							data="therapists"
							fallback={
								<Skeleton className="col-span-full md:col-span-1 h-[360px] rounded-md" />
							}
						>
							{therapists?.by_gender && (
								<TherapistByGenderChart
									data={therapists.by_gender}
									className="col-span-full md:col-span-1"
								/>
							)}
						</Deferred>
					</div>
				</section>
			</PageContainer>
		</>
	);
}
